// SPDX-License-Identifier: AGPL-3.0-or-later
// A durable payload is committed before its composer draft is cleared. Files live in IndexedDB,
// together with the intent, so reload cannot split a message from its attachments.

import {getProtectedIndexedDB} from '@app/features/platform/state/ProtectedWebStorage';
import {i18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import type {AgentOutgoing, AgentPendingStart} from './Agents';

type Payload = {kind: 'message'; value: AgentOutgoing} | {kind: 'start'; value: AgentPendingStart};
type SavedFile = {blob: Blob; name: string; type: string; lastModified: number};
type RecordValue = {
	owner: string;
	id: string;
	kind: Payload['kind'];
	value: Record<string, unknown>;
	files: Array<SavedFile>;
};
let writes: Promise<unknown> = Promise.resolve();

async function database(): Promise<IDBDatabase> {
	const factory = getProtectedIndexedDB();
	if (!factory)
		throw new Error(i18n._(msg({message: 'Local message storage is unavailable. Your draft has been kept.'})));
	return new Promise((resolve, reject) => {
		const request = factory.open('konvo-agent-outbox', 1);
		request.onupgradeneeded = () => request.result.createObjectStore('messages', {keyPath: ['owner', 'id']});
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
		request.onblocked = () =>
			reject(new Error(i18n._(msg({message: 'Local message storage is blocked. Your draft has been kept.'}))));
	});
}
function transaction<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
	const next = writes
		.catch(() => undefined)
		.then(async () => {
			const db = await database();
			return new Promise<T>((resolve, reject) => {
				const tx = db.transaction('messages', mode);
				const request = run(tx.objectStore('messages'));
				tx.oncomplete = () => {
					db.close();
					resolve(request.result);
				};
				tx.onabort = tx.onerror = () => {
					db.close();
					reject(tx.error ?? request.error);
				};
			});
		});
	writes = next;
	return next;
}
export async function saveOutgoing(owner: string, payload: Payload): Promise<void> {
	const {files, error, ...value} = payload.value;
	const failure =
		error && typeof error === 'object'
			? {
					message: error instanceof Error ? error.message : String(error),
					...('body' in error ? {body: error.body} : {}),
					...('status' in error ? {status: error.status} : {}),
				}
			: error;
	const record: RecordValue = {
		owner,
		id: value.id,
		kind: payload.kind,
		value: JSON.parse(
			JSON.stringify({...value, error: failure}, (_key, item) => (item instanceof Promise ? undefined : item)),
		),
		files: files.map((file) => ({blob: file, name: file.name, type: file.type, lastModified: file.lastModified})),
	};
	await transaction('readwrite', (store) => store.put(record));
}
export async function deleteOutgoing(owner: string, id: string): Promise<void> {
	await transaction('readwrite', (store) => store.delete([owner, id]));
}
export async function loadOutgoing(owner: string): Promise<Array<Payload>> {
	const records = (await transaction('readonly', (store) =>
		store.getAll(IDBKeyRange.bound([owner, ''], [owner, '\uffff'])),
	)) as Array<RecordValue>;
	return records
		.filter((record) => record.owner === owner)
		.map(
			(record) =>
				({
					kind: record.kind,
					value: {
						...record.value,
						files: record.files.map(
							(file) => new File([file.blob], file.name, {type: file.type, lastModified: file.lastModified}),
						),
					},
				}) as unknown as Payload,
		);
}
